import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AlertFacade } from '@curbnturf/alert';
import { listingComplete } from '@curbnturf/helpers';
import { ILandType, Site } from '@curbnturf/objects';
import { SiteFacade } from '@curbnturf/site';
import { IonicModule, ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

@Component({
  selector: 'curbnturf-edit-site-name-modal',
  standalone: true,
  imports: [IonicModule, ReactiveFormsModule],
  templateUrl: './edit-site-name-modal.component.html',
  styleUrl: './edit-site-name-modal.component.scss',
})
export class EditSiteNameModalComponent implements OnInit {
  @Input() site: Site;

  subscriptions: Subscription[] = [];

  form: FormGroup<{
    name: FormControl<string | null>;
    description: FormControl<string | null>;
  }>;

  landTypesForm: FormArray<FormControl<ILandType['type'] | null>>;

  listingComplete = listingComplete;

  get description() {
    if (this.form) {
      return this.form.controls.description;
    }

    return;
  }

  get descriptionLength(): number {
    if (this.description) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((this.description as any)._pendingValue) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (this.description as any)._pendingValue.length;
      }

      return this.description.value?.length || 0;
    }

    return 0;
  }

  get name() {
    if (this.form) {
      return this.form.controls.name;
    }

    return;
  }

  constructor(
    private fb: FormBuilder,
    private modalController: ModalController,
    private siteFacade: SiteFacade,
    private alertFacade: AlertFacade,
  ) {}

  async close() {
    await this.modalController.dismiss();
  }

  ngOnInit() {
    this.makeForms();
  }

  ngOnChanges() {
    this.makeForms();
  }

  makeForms() {
    this.form = this.fb.group({
      name: this.fb.control<string | null>('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(32),
      ]),
      description: this.fb.control<string | null>(''),
    });

    this.landTypesForm = this.fb.array<FormControl<ILandType['type'] | null>>([]);

    if (this.site) {
      this.form.patchValue({
        description: this.site.description,
        name: this.site.name,
      });
    }

    this.subscriptions.push(
      this.form.valueChanges
        .pipe(
          tap(() => {
            this.form.markAsTouched();
            this.form.markAsDirty();
            this.name?.markAsTouched();
            this.name?.markAsDirty();
            this.description?.markAsTouched();
            this.description?.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.saveDescription()),
    );
  }

  saveDescription() {
    if (this.form.valid && this.site.id) {
      // Update the user record with the following data
      this.siteFacade.updateDescription(this.site.id, {
        name: this.name?.value as string,
        description: this.description?.value === null ? undefined : this.description?.value,
        available: this.site.available,
      });
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }
}
